import { journalEntriesAPI } from '../lib/api';
import { getCurrentCompanyId } from '../utils/auth';

// Mock data fallback for when API calls fail
const mockJournalEntries = [
  {
    id: 1,
    entryNumber: 'JE-2026-001',
    date: '2026-01-15',
    description: 'Opening Balance',
    totalDebit: 10000,
    totalCredit: 10000,
    status: 'posted',
    companyId: 1,
    createdAt: '2026-01-15T10:00:00Z'
  },
  {
    id: 2,
    entryNumber: 'JE-2026-002',
    date: '2026-01-16',
    description: 'Monthly Rent Payment',
    totalDebit: 2500,
    totalCredit: 2500,
    status: 'posted',
    companyId: 1,
    createdAt: '2026-01-16T14:00:00Z'
  }
];

export const getJournalEntries = async (companyId = null) => {
  try {
    const actualCompanyId = companyId || getCurrentCompanyId();
    console.log('🏢 Using company ID:', actualCompanyId);
    
    if (!actualCompanyId) {
      console.warn('⚠️ No company ID available, using mock data');
      return mockJournalEntries;
    }
    
    console.log('🌐 Making API call to get journal entries...');
    const response = await journalEntriesAPI.getAll(actualCompanyId);
    console.log('📡 Raw API response:', response);
    console.log('📡 Response data:', response.data);
    console.log('📡 Response status:', response.status);
    
    let journalEntries = [];
    
    // Handle the API response format: { Success: true, Data: [...], Message: "...", Errors: [] }
    if (response.data && response.data.Success && Array.isArray(response.data.Data)) {
      console.log('✅ Found Success=true with Data array, length:', response.data.Data.length);
      journalEntries = response.data.Data;
    } else if (Array.isArray(response.data)) {
      console.log('✅ Found direct array response, length:', response.data.length);
      journalEntries = response.data;
    } else {
      console.warn('⚠️ Unexpected response format:', typeof response.data, response.data);
      console.warn('⚠️ Using mock data instead');
      return mockJournalEntries;
    }
    
    console.log('🔄 Raw journal entries before mapping:', journalEntries);
    
    // Map API field names to frontend expected field names
    const mappedEntries = journalEntries.map((entry, index) => {
      console.log(`🔄 Mapping entry ${index + 1}:`, entry);
      
      const mapped = {
        id: entry.Id || entry.id,
        entryNumber: entry.EntryNumber || entry.entryNumber,
        entryDate: entry.EntryDate || entry.entryDate,
        description: entry.Description || entry.description,
        reference: entry.Reference || entry.reference,
        totalDebit: entry.TotalDebit || entry.totalDebit || 0,
        totalCredit: entry.TotalCredit || entry.totalCredit || 0,
        status: entry.Status || entry.status,
        companyId: entry.CompanyId || entry.companyId,
        userId: entry.UserId || entry.userId,
        createdAt: entry.CreatedAt || entry.createdAt,
        updatedAt: entry.UpdatedAt || entry.updatedAt,
        lineItems: (entry.LineItems || entry.lineItems || []).map(lineItem => ({
          id: lineItem.Id || lineItem.id,
          journalEntryId: lineItem.JournalEntryId || lineItem.journalEntryId,
          accountId: lineItem.AccountId || lineItem.accountId,
          account: lineItem.AccountId || lineItem.accountId, // For table display
          description: lineItem.Description || lineItem.description,
          debit: lineItem.DebitAmount || lineItem.debitAmount || 0, // Map to expected field name
          credit: lineItem.CreditAmount || lineItem.creditAmount || 0, // Map to expected field name
          debitAmount: lineItem.DebitAmount || lineItem.debitAmount || 0,
          creditAmount: lineItem.CreditAmount || lineItem.creditAmount || 0,
          memo: lineItem.Description || lineItem.description, // Use description as memo
          lineNumber: lineItem.LineNumber || lineItem.lineNumber,
          createdAt: lineItem.CreatedAt || lineItem.createdAt
        }))
      };
      
      console.log(`✅ Mapped entry ${index + 1}:`, mapped);
      return mapped;
    });
    
    console.log('✅ Final mapped journal entries:', mappedEntries);
    console.log('✅ Returning', mappedEntries.length, 'entries');
    return mappedEntries;
  } catch (error) {
    console.error('❌ Error in getJournalEntries:', error);
    console.error('❌ Error details:', error.response?.data);
    console.warn('⚠️ Using mock data due to error');
    return mockJournalEntries;
  }
};

export const createJournalEntry = async (entryData) => {
  try {
    const companyId = getCurrentCompanyId();
    const userId = JSON.parse(localStorage.getItem('current_user'))?.id;
    
    if (!companyId || !userId) {
      throw new Error('Company ID or User ID not found');
    }

    // Transform the form data to match the API payload structure
    const payload = {
      companyId: companyId,
      userId: userId,
      entryNumber: entryData.reference || `JE-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`,
      entryDate: entryData.entryDate,
      description: entryData.description,
      reference: entryData.reference,
      totalDebit: entryData.lineItems.reduce((sum, item) => sum + (parseFloat(item.debit) || 0), 0),
      totalCredit: entryData.lineItems.reduce((sum, item) => sum + (parseFloat(item.credit) || 0), 0),
      status: "Draft",
      lineItems: entryData.lineItems.map((item, index) => ({
        accountId: item.account,
        description: item.memo || entryData.description,
        debitAmount: parseFloat(item.debit) || 0,
        creditAmount: parseFloat(item.credit) || 0,
        lineNumber: index + 1
      }))
    };

    console.log('Creating journal entry with payload:', payload);
    const response = await journalEntriesAPI.create(payload);
    
    // Handle the API response format: { Success: true, Data: {...}, Message: "...", Errors: [] }
    if (response.data && response.data.Success) {
      console.log('Journal entry created successfully:', response.data.Message);
      return response.data.Data;
    }
    
    return response.data;
  } catch (error) {
    console.error('Error creating journal entry:', error);
    throw error;
  }
};

export const updateJournalEntry = async (id, entryData) => {
  try {
    const response = await journalEntriesAPI.update(id, entryData);
    return response.data;
  } catch (error) {
    console.error('Error updating journal entry:', error);
    throw error;
  }
};

export const deleteJournalEntry = async (id) => {
  try {
    console.log('🗑️ Deleting journal entry with ID:', id);
    
    const response = await journalEntriesAPI.delete(id);
    console.log('📡 Delete response:', response);
    
    // Handle the API response format: { Success: true, Message: "...", Data: null, Errors: [] }
    if (response.data && response.data.Success) {
      console.log('✅ Journal entry deleted successfully:', response.data.Message);
      return true;
    }
    
    // If no specific success format, assume success if no error was thrown
    console.log('✅ Journal entry deleted (assumed success)');
    return true;
  } catch (error) {
    console.error('❌ Error deleting journal entry:', error);
    console.error('❌ Error response:', error.response?.data);
    
    // Provide more specific error messages
    if (error.response?.status === 404) {
      throw new Error('Journal entry not found. It may have already been deleted.');
    } else if (error.response?.status === 403) {
      throw new Error('You do not have permission to delete this journal entry.');
    } else if (error.response?.status === 400) {
      throw new Error('Cannot delete this journal entry. It may be posted or have dependencies.');
    } else {
      throw new Error('Failed to delete journal entry. Please try again.');
    }
  }
};

export const postJournalEntry = async (id) => {
  try {
    const response = await journalEntriesAPI.post(id);
    return response.data;
  } catch (error) {
    console.error('Error posting journal entry:', error);
    throw error;
  }
};

export const validateJournalEntryBalance = async (entryData) => {
  try {
    const response = await journalEntriesAPI.validateBalance(entryData);
    return response.data;
  } catch (error) {
    console.error('Error validating journal entry balance:', error);
    throw error;
  }
};

export const getGeneralLedger = async (companyId = null) => {
  try {
    const actualCompanyId = companyId || getCurrentCompanyId();
    if (!actualCompanyId) {
      console.warn('No company ID available, using mock data');
      return [];
    }
    
    // Mock general ledger data
    return [
      {
        id: 1,
        date: '2026-01-15',
        account: 'Cash',
        description: 'Opening Balance',
        debit: 10000,
        credit: 0,
        balance: 10000
      },
      {
        id: 2,
        date: '2026-01-16',
        account: 'Rent Expense',
        description: 'Monthly Rent Payment',
        debit: 2500,
        credit: 0,
        balance: 2500
      }
    ];
  } catch (error) {
    console.error('Error in getGeneralLedger:', error);
    return [];
  }
};